// RegisterModal.js
import React, { useState } from 'react';
import apiService, { errorHelpers, validationHelpers } from '../../utils/apiService';

const RegisterModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        firstname: '',
        middlename: '',
        lastname: '',
        email: '',
        phone: '',
        address: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
        setAlert(null);
    };

    const validateForm = () => {
        const newErrors = {};

        // Required fields validation
        if (!formData.firstname.trim()) {
            newErrors.firstname = 'First name is required';
        }

        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validationHelpers.validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!validationHelpers.validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else {
            const passwordValidation = validationHelpers.validatePassword(formData.password);
            if (!passwordValidation.isValid) {
                const errorMessages = Object.values(passwordValidation.errors).filter(Boolean);
                newErrors.password = errorMessages[0]; // Show first error
            }
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            // Remove confirmPassword from submission data
            const { confirmPassword, ...submitData } = formData;

            const response = await apiService.auth.register(submitData);

            setAlert({
                type: 'success',
                message: response.data.message
            });

            // Clear form
            setFormData({
                firstname: '',
                middlename: '',
                lastname: '',
                email: '',
                phone: '',
                address: '',
                password: '',
                confirmPassword: ''
            });

            // Close modal after delay
            setTimeout(() => {
                onClose();
            }, 3000);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal" style={{ maxWidth: '600px' }}>
                <div className="modal-header">
                    <h2 className="modal-title">Create Your Account</h2>
                    <button className="modal-close" onClick={onClose} type="button">
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    {alert && (
                        <div className={`alert alert-${alert.type}`}>
                            {alert.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Name Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <label className="form-label" htmlFor="firstname">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    id="firstname"
                                    name="firstname"
                                    className={`form-input ${errors.firstname ? 'error' : ''}`}
                                    value={formData.firstname}
                                    onChange={handleChange}
                                    placeholder="Enter first name"
                                    disabled={loading}
                                />
                                {errors.firstname && (
                                    <div className="form-error">{errors.firstname}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="lastname">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    id="lastname"
                                    name="lastname"
                                    className={`form-input ${errors.lastname ? 'error' : ''}`}
                                    value={formData.lastname}
                                    onChange={handleChange}
                                    placeholder="Enter last name"
                                    disabled={loading}
                                />
                                {errors.lastname && (
                                    <div className="form-error">{errors.lastname}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="middlename">
                                Middle Name (Optional)
                            </label>
                            <input
                                type="text"
                                id="middlename"
                                name="middlename"
                                className="form-input"
                                value={formData.middlename}
                                onChange={handleChange}
                                placeholder="Enter middle name"
                                disabled={loading}
                            />
                        </div>

                        {/* Contact Fields */}
                        <div className="form-group">
                            <label className="form-label" htmlFor="email">
                                Email Address *
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className={`form-input ${errors.email ? 'error' : ''}`}
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                                disabled={loading}
                            />
                            {errors.email && (
                                <div className="form-error">{errors.email}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="phone">
                                Phone Number *
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className={`form-input ${errors.phone ? 'error' : ''}`}
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter phone number"
                                disabled={loading}
                            />
                            {errors.phone && (
                                <div className="form-error">{errors.phone}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="address">
                                Address *
                            </label>
                            <div style={{ marginBottom: '8px' }}>
                                <small style={{
                                    color: '#6b7280',
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    backgroundColor: '#f8fafc',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    💡 <strong>Format:</strong> Company Name, Address Line 1, Address Line 2, City, State, Country
                                    <br />
                                    <span style={{ color: '#059669', fontWeight: '500' }}>
                                        The first part before the comma will be used as your company name on the verification letters.
                                    </span>
                                </small>
                            </div>
                            <textarea
                                id="address"
                                name="address"
                                className={`form-textarea ${errors.address ? 'error' : ''}`}
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="e.g., ABC Company Ltd, 123 Main Street, Suite 456, Lagos, Lagos State, Nigeria"
                                disabled={loading}
                                rows="3"
                            />
                            {errors.address && (
                                <div className="form-error">{errors.address}</div>
                            )}
                        </div>

                        {/* Password Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-group">
                                <label className="form-label" htmlFor="password">
                                    Password *
                                </label>
                                <div className="password-input-container" style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className={`form-input ${errors.password ? 'error' : ''}`}
                                        style={{ paddingRight: '40px' }}
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Enter password"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            fontSize: '16px',
                                            color: '#666',
                                            zIndex: 10
                                        }}
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={loading}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="form-error">{errors.password}</div>
                                )}
                                <div className="text-xs text-gray-500 mt-1">
                                    Password must be at least 8 characters with uppercase, lowercase, and number
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="confirmPassword">
                                    Confirm Password *
                                </label>
                                <div className="password-input-container" style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                        style={{ paddingRight: '40px' }}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="Confirm password"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0',
                                            fontSize: '16px',
                                            color: '#666',
                                            zIndex: 10
                                        }}
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={loading}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <div className="form-error">{errors.confirmPassword}</div>
                                )}
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-block mt-6"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading-spinner mr-2"></span>
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-4 text-sm text-gray-600">
                        By creating an account, you agree to our Terms of Service and Privacy Policy.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterModal;